import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useBillingBlockStore } from '@/stores/billing-block.store';
import { useAuthStore } from '@/stores/auth.store';
import { usePermissions } from '@/hooks/usePermissions';
import { useBillingAccess } from '@/hooks/useBilling';
import { PaywallScreen } from './PaywallScreen';
import { AccountUnavailableScreen } from './AccountUnavailableScreen';

interface BillingGateProps {
  children: ReactNode;
}

/**
 * Rotas do "surface" de billing que DEVEM permanecer acessíveis mesmo com o
 * paywall (402) ativo. Sem isto, um cliente com trial expirado / inadimplente
 * fica em deadlock: o paywall cobre TODAS as rotas e ele nunca chega à tela de
 * Faturamento (`/billing`) para regularizar. Espelha a allowlist de
 * `/billing/*` do `BillingStatusGuard` no backend (issue #884 §9 / D5).
 *
 * Obs.: a rota `/billing` é gated pela feature flag `billing_enabled` (o billing
 * é "ligado" por ela). Esta allowlist só garante que o `BillingGate` não cubra
 * `/billing` com o paywall — a flag precisa estar ATIVA para o tenant para a
 * rota renderizar (senão o `ProtectedRoute` redireciona).
 *
 * NÃO se aplica ao gate de org (`org_suspended` / 403): esse é administrativo
 * e pagar não resolve, então cobre tudo, inclusive o billing.
 */
const BILLING_SURFACE_PREFIXES = ['/billing'] as const;

function isBillingSurface(pathname: string): boolean {
  return BILLING_SURFACE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * BillingGate — gate de renderização de billing no frontend (Epic #884 §3.1).
 *
 * Quando o interceptor Axios sinaliza bloqueio (402 paywall ou 403
 * `ORG_SUSPENDED`), substitui a árvore de rotas pela tela apropriada:
 *  - `paywall` + billing owner → redireciona para `/billing` (o responsável vai
 *    direto reativar a assinatura).
 *  - `paywall` + não-owner → `PaywallScreen` (sem CTA de pagamento; pede para
 *    contatar o responsável financeiro). EXCETO no surface de billing
 *    (`/billing*`), que continua acessível.
 *  - `org_suspended` → `AccountUnavailableScreen` (neutra, sem CTA) — cobre tudo.
 *
 * DESBLOQUEIO (anti-deadlock): enquanto bloqueado, TODAS as requests
 * não-allowlistadas são rejeitadas no interceptor (api.ts), então o caminho
 * legado de "limpar em 2xx não-allowlistado" não dispara. Para não trancar o
 * cliente, montamos `useBillingAccess` aqui (endpoint allowlistado, sempre
 * passa) quando autenticado: ao reportar `allowed`/`trial` ele limpa o store →
 * requests voltam a fluir; ao reportar `paywall`/`org_suspended` ele mantém o
 * bloqueio.
 */
export function BillingGate({ children }: BillingGateProps) {
  const { blocked, kind, code, message } = useBillingBlockStore();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { pathname } = useLocation();
  const { canAccessRoute, isMaster, isOrgOwner } = usePermissions();

  // Platform admins (MASTER / ORG_OWNER) NUNCA são pagantes — billing/trial/
  // inadimplência são por TENANT de CRM. Eles operam no master panel (`/system`,
  // hostname `painel.*`) e o gate de billing não se aplica. Espelha o bypass do
  // backend (`BillingStatusGuard` e `GET /billing/access`).
  const isPlatformAdmin = isMaster || isOrgOwner;

  // Mantém `/billing/access` montado/revalidável enquanto autenticado — é a
  // ÚNICA fonte capaz de LIMPAR o bloqueio quando tudo o mais está bloqueado
  // (ver useBillingAccess). Desabilitado quando deslogado para evitar 401 em
  // loop no /login (o endpoint exige autenticação) e para platform admins, que
  // não têm contexto de billing per-tenant.
  const { data: access } = useBillingAccess(
    isAuthenticated && !isPlatformAdmin,
  );
  const isBillingOwner = access?.isBillingOwner === true;

  // Platform admins nunca veem paywall/account-unavailable — early-return ANTES
  // de qualquer checagem de bloqueio. O painel master sempre renderiza.
  if (isPlatformAdmin) {
    return <>{children}</>;
  }

  // Gate de ciclo de vida da ORG (403): bloqueia TUDO — pagar não resolve.
  if (blocked && kind === 'org_suspended') {
    return <AccountUnavailableScreen message={message} />;
  }

  // Paywall do TENANT (402): bloqueia tudo, EXCETO o surface de billing.
  if (blocked && kind === 'paywall' && !isBillingSurface(pathname)) {
    // Billing owner vai direto à tela de Faturamento para reativar a assinatura.
    // (Enquanto `/billing/access` carrega, `isBillingOwner` é `false` → mostramos
    // a PaywallScreen do não-owner até resolver, sem crashar.)
    //
    // DEFESA EM PROFUNDIDADE (anti-loop): só redireciona quando o owner consegue
    // de fato ALCANÇAR `/billing`. Um billing owner pode ter qualquer role CRM
    // (não só admin), então a rota `/billing` aceita todas elas. Se a rota voltar
    // a ser restrita no futuro e o owner não puder alcançá-la, `canAccessRoute`
    // retorna `false` e mostramos a PaywallScreen — NUNCA entramos em loop
    // (BillingGate → /billing → reject → rota anterior → BillingGate → …).
    if (isBillingOwner && canAccessRoute('/billing')) {
      return <Navigate to="/billing" replace />;
    }
    return <PaywallScreen code={code} message={message} isBillingOwner={false} />;
  }

  return <>{children}</>;
}
